import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, Box } from "@mui/material";
import type { UserDto } from "client/api/UsersApi";
import { deleteUser, updateUser } from "client/api/UsersApi";
import { ActionDialog } from "components/ActionDialog/ActionDialog";
import { useEntity } from "components/EntityProvider/EntityProvider";

export function UserDelete() {
  const { entity: user, refresh: refreshUser } = useEntity<UserDto>();
  const navigate = useNavigate();

  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [redactDialogOpen, setRedactDialogOpen] = useState(false);
  const [isRedacting, setIsRedacting] = useState(false);
  const [redactError, setRedactError] = useState<string | null>(null);

  if (!user) return <></>;

  const isAlreadyRedacted =
    user.firstName === "[Redacted]" &&
    user.lastName === "[Redacted]" &&
    user.email === `deleted-${user.id}@anonymized.local`;

  if (user.email?.endsWith("@octopus.com")) {
    return (
      <Grid container spacing={3}>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant={"h5"}>Delete User</Typography>
        </Grid>
        <Grid size={12}>
          <Alert severity={"info"}>This user cannot be deleted as they are an Octopus employee</Alert>
        </Grid>
      </Grid>
    );
  }

  const onDeleteUser = async () => {
    const success = await deleteUser(user.id);
    if (success) {
      navigate(`/users`);
    }
  };

  const handleRedactUser = async () => {
    setIsRedacting(true);
    setRedactError(null);
    try {
      await updateUser(user.id, {
        firstName: "[Redacted]",
        lastName: "[Redacted]",
        email: `deleted-${user.id}@anonymized.local`,
      });
      await refreshUser?.();
      setRedactDialogOpen(false);
    } catch (error) {
      setRedactError("Failed to redact user information. Please try again.");
    } finally {
      setIsRedacting(false);
    }
  };

  function confirmDeleteUser() {
    setShowDialog(true);
  }

  function cancelDeleteUser() {
    setShowDialog(false);
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Typography variant={"h5"}>Delete User</Typography>
        </Grid>
        <Grid size={12}>
          <Alert severity={"warning"}>
            Deleting a user or redacting their PII is permanent and cannot be reversed, use this form in the case that
            they've asked for their information to be removed in compliance with GDPR or similar. Deletion is only
            possible if the user is not assigned to a subscription as an owner.
          </Alert>
        </Grid>
        <Grid size={12}>
          {!isAlreadyRedacted && (
            <Button variant={"outlined"} color={"error"} onClick={() => setRedactDialogOpen(true)} sx={{ mr: 2 }}>
              Redact PII
            </Button>
          )}
          <Button variant={"contained"} color={"warning"} onClick={confirmDeleteUser}>
            Delete User
          </Button>
        </Grid>
      </Grid>
      <ActionDialog
        title={`Delete ${user.email}?`}
        description={"Are you sure you want to delete this user? This is a permanent action and cannot be reversed."}
        show={showDialog}
        confirmText={"Delete"}
        onConfirm={onDeleteUser}
        onCancel={cancelDeleteUser}
      />
      <Dialog
        open={redactDialogOpen}
        onClose={() => {
          if (!isRedacting) {
            setRedactDialogOpen(false);
            setRedactError(null);
          }
        }}
      >
        <DialogTitle>Redact Personal Information</DialogTitle>
        <DialogContent>
          {redactError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {redactError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently redact all personal information (email, first name, last name). This cannot be
            undone.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <strong>Current Information:</strong>
            <ul>
              <li>Email: {user.email}</li>
              <li>First Name: {user.firstName}</li>
              <li>Last Name: {user.lastName || "(none)"}</li>
            </ul>
            <strong>After Redaction:</strong>
            <ul>
              <li>Email: deleted-{user.id}@anonymized.local</li>
              <li>First Name: [Redacted]</li>
              <li>Last Name: [Redacted]</li>
            </ul>
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Note:</strong> If this user signs in again, their personal information (email, first name, last
              name) will be automatically repopulated from the authentication provider as part of the login/token
              validation process. To keep this redaction permanent, you must also prevent the user from signing in again
              (for example, by disabling or removing their account in the authentication provider).
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRedactDialogOpen(false);
              setRedactError(null);
            }}
            disabled={isRedacting}
          >
            Cancel
          </Button>
          <Button onClick={handleRedactUser} color="error" variant="contained" disabled={isRedacting}>
            {isRedacting ? "Redacting..." : "Confirm Redaction"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
